import React from 'react';
import styled, { keyframes } from 'styled-components';
import Link from '../components/Link';
import { lighten, darken } from 'polished';

import {
	arrowRightIcon,
	xIcon,
	loadingIcon,
	hamburgerIcon,
} from '../media/icons';

export const Button = styled.button`
	color: ${p => p.theme.colors.buttonText};
	background-color: ${p => p.theme.colors.buttonBg};
	border: none;
	cursor: pointer;
	display: inline-block;
	font-size: 1rem;
	font-weight: 700;
	line-height: 1.22;
	letter-spacing: 0.2px;
	padding: 15px;
	position: relative;
	transition: background-color 0.5s;

	&:hover,
	&:active {
		background-color: ${p => darken(0.1, p.theme.colors.buttonBg)};
	}

	&:disabled {
		background-color: ${p => lighten(0.4, p.theme.colors.buttonBg)};
		cursor: wait;
	}
`;

export const LinkButton = styled(Link)`
	color: ${p => p.theme.colors.primary};
	background-color: ${p => lighten(0.4, p.theme.colors.buttonBg)};
	border: none;
	cursor: pointer;
	display: inline-block;
	font-size: 1rem;
	font-weight: 700;
	line-height: 1.22;
	letter-spacing: 0.2px;
	padding: 10px 41px;
	text-decoration: none;
	transition: background-color 0.4s, color 0.4s;

	&:hover,
	&:active {
		background-color: ${p => p.theme.colors.buttonBg};
		color: ${p => lighten(0.4, p.theme.colors.buttonBg)};
	}
`;

export const DarkButton = styled(Button)`
	color: ${p => p.theme.colors.white};
	background-color: ${p => p.theme.colors.darkButtonBg};

	&:hover,
	&:active {
		background-color: ${p => p.theme.colors.black};
	}
`;

export const PrimaryLinkButton = styled(LinkButton)`
	color: ${p => p.theme.colors.white};
	background-color: ${p => p.theme.colors.primary};

	&:hover,
	&:active {
		background-color: ${p => darken(0.1, p.theme.colors.buttonBg)};
	}
`;

export const DarkLinkButton = styled(LinkButton)`
	color: ${p => p.theme.colors.white};
	background-color: ${p => p.theme.colors.darkButtonBg};

	&:hover,
	&:active {
		background-color: ${p => p.theme.colors.black};
		color: ${p => darken(0.1, p.theme.colors.white)};
	}
`;

export const HollowButton = styled.button`
	background-color: transparent;
	border: 1px solid ${p => p.theme.colors.darkBg};
	color: ${p => p.theme.colors.darkFont};
	cursor: pointer;
	font-size: 1rem;
	font-weight: 700;
	line-height: 1.19;
	letter-spacing: 0.2px;
	text-align: center;
	padding: 8px 38px;
	transition: background-color 0.5s, color 0.5s;

	&:hover,
	&:active {
		background-color: ${p => p.theme.colors.darkBg};
		color: ${p => p.theme.colors.white};
	}
`;

export const LargeButton = styled(Button)`
	font-size: 1.25rem;
	line-height: 1.2;
	padding: 20px 25px;
`;

export const WideButton = styled(Button)`
	padding: 10px 54px;
`;

export const DarkWideButton = styled(WideButton)`
	color: ${p => p.theme.colors.white};
	background-color: ${p => p.theme.colors.darkButtonBg};

	&:hover,
	&:active {
		background-color: ${p => p.theme.colors.black};
	}
`;

export const TextButton = styled(Button)`
	background: transparent;
	border: none;
	color: ${p => p.theme.colors.lightFont};
	font-size: 1rem;
	line-height: 1.38;
	padding: 10px 0;
	text-decoration: underline;

	&:hover,
	&:active {
		background: transparent;
		color: ${p => p.theme.colors.black};
	}
`;

export const LoadButton = styled.button`
	background-color: ${p => p.theme.colors.lightBg};
	border: 1px solid ${p => p.theme.colors.border};
	background-image: url(${arrowRightIcon});
	background-position: 95% 50%;
	background-repeat: no-repeat;
	background-size: 14px;
	color: ${p => p.theme.colors.lightFont};
	cursor: pointer;
	display: block;
	font-size: 1.125rem;
	font-weight: 500;
	line-height: 1;
	letter-spacing: 0.2px;
	margin: 0 auto;
	max-width: 490px;
	padding: 23px 20px 20px 20px;
	text-align: left;
	transition: 0.5s background-position, 0.5s background-color;
	width: 100%;

	&:hover,
	&:active {
		color: ${p => p.theme.colors.darkFont};
		background-color: ${p =>
			p.theme.colors.lightBg && darken(0.1, p.theme.colors.lightBg)};
		background-position: 96% 50%;
	}

	&:disabled {
		cursor: progress;
	}
`;

export const ClearButton = styled.button`
	background-color: transparent;
	background-image: url(${xIcon});
	background-position: 50% 50%;
	background-repeat: no-repeat;
	background-size: 13px;
	border: none;
	cursor: pointer;
	padding: 13px;
	text-indent: -9999px;
`;

const StyledRemoveButton = styled.button`
	border: none;
	background: none;
	cursor: pointer;
	margin: 0;
	padding: 0;
	transition: color 0.5s;

	&:hover,
	&:active {
		color: ${p => p.theme.colors.error};
	}
`;

export function RemoveButton(props) {
	return <StyledRemoveButton {...props}>&times;</StyledRemoveButton>;
}

export const MenuButton = styled.button`
	background-color: transparent;
	background-image: url(${hamburgerIcon});
	background-position: 50%;
	background-repeat: no-repeat;
	background-size: 30px 26px;
	border: none;
	cursor: pointer;
	height: 26px;
	position: relative;
	width: 30px;
	z-index: 9999;
`;

const rotate360 = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const Icon = styled.img`
	animation: ${rotate360} 1s linear infinite;
	display: block;
	margin: 0 auto 5px auto;
	position: absolute;
	top: 20%;
	right: 0;
	bottom: 0;
	left: 0;
	width: 30px;
`;

const Text = styled.span`
	opacity: ${p => p.loading};
	transition: opacity 0.1s;
`;

export function LoadingButton({ loading, children, ...rest }) {
	return (
		<Button {...rest} disabled={loading}>
			{loading && <Icon src={loadingIcon} />}
			<Text loading={loading}>{children}</Text>
		</Button>
	);
}

export function LargeLoadingButton({ loading, children, ...rest }) {
	console.log(typeof loading);
	return (
		<LargeButton {...rest} disabled={loading}>
			{loading && <Icon src={loadingIcon} />}
			<Text loading={loading ? '0' : '1'}>{children}</Text>
		</LargeButton>
	);
}
